import type { EventType, Participation, SlotKey } from './api';

export const DM_TEXT_MAX = 500;

const EVENT_TYPE_SHORT: Record<EventType, string> = {
  canyon_storm: 'CS',
  desert_storm: 'DS',
  special_ingame: 'Event',
};

function playerLabel(p: Pick<Participation, 'player'>) {
  return (p.player.displayName?.trim() || p.player.name).trim();
}

/**
 * Build the DM text for a slot. Mirrors the CS/DS plan sub-pairing rule
 * so what subs see matches the battle map: non-flex starters ranked by M1
 * desc, top N are the protected core (PT core for CS = 4, Silo core for
 * DS = 2), remaining sub-eligible pool has its N weakest paired with the
 * N subs sorted M1 desc.
 */
export function buildSlotDmText(opts: {
  eventType: EventType;
  slot: SlotKey;
  startsAtServer: string | undefined;
  starters: Participation[];
  subs: Participation[];
}): string {
  const { eventType, slot, startsAtServer, starters, subs } = opts;
  const m1Of = (p: Participation) => p.player.latestSnapshot?.m1Power ?? 0;
  const byM1Desc = (a: Participation, b: Participation) => m1Of(b) - m1Of(a);

  const sortedSubs = [...subs].sort(byM1Desc);
  const nonFlexStarters = [...starters].filter((p) => !p.flex).sort(byM1Desc);
  const coreCount = eventType === 'canyon_storm' ? 4 : 2;
  const coreIds = new Set(nonFlexStarters.slice(0, coreCount).map((p) => p.playerId));
  const eligible = nonFlexStarters.filter(
    (p) => !coreIds.has(p.playerId) && !p.player.neverSub,
  );
  const weakest = [...eligible]
    .sort((a, b) => m1Of(a) - m1Of(b))
    .slice(0, sortedSubs.length);
  const paired = weakest.sort(byM1Desc);
  const timePart = startsAtServer ? ` at ST ${startsAtServer}` : '';
  const heading = `${EVENT_TYPE_SHORT[eventType]} ${slot}${timePart}`;
  const intro = 'Follow the plan. Subs: DM your assigned starter to coordinate.';
  const hint =
    'If the fight allows and personal points are reached, sub in around the 15-min mark.';
  const lines = sortedSubs.map((sub, i) => {
    const starter = paired[i];
    const left = starter ? playerLabel(starter) : '—';
    return `${i + 1}. ${left} → ${playerLabel(sub)}`;
  });
  let out = [heading, intro, hint, '', ...lines].join('\n');
  if (out.length > DM_TEXT_MAX) out = out.slice(0, DM_TEXT_MAX - 1) + '…';
  return out;
}
